import type { QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';

import type { FileRef } from '@/shared/api/types';
import { getAuthState, useAuthStore } from '@/shared/auth/store';
import { REALTIME_RECONNECTED, type RealtimeManager } from '@/shared/realtime';

import { applyCustomerAcks, openlinesKeys, upsertMessage } from './api';
import type {
  CustomerTypingPayload,
  Message,
  SessionChangedPayload,
  SessionDetails,
  SessionReadPayload,
} from './types';

/** Realtime event types of open lines (docs/modules/openlines.md). */
export const OpenlinesEvents = {
  sessionChanged: 'openlines.session-changed',
  sessionRemoved: 'openlines.session-removed',
  messageCreated: 'openlines.message-created',
  messageUpdated: 'openlines.message-updated',
  sessionRead: 'openlines.session-read',
  typing: 'openlines.typing',
  configChanged: 'openlines.config-changed',
} as const;

/** The topic of an open dialog. */
export const sessionTopic = (sessionId: string) => `openlines-session:${sessionId}`;

/** How long "the client is typing" stays after the last signal. */
export const TYPING_MS = 5000;

/** The operator's unsent answer or note of a dialog (kept while they switch between dialogs). */
export interface ComposerDraft {
  text: string;
  note: boolean;
  attachments: FileRef[];
}

interface OpenlinesState {
  /** Session → who types and until when (the client of the dialog). */
  typing: Record<string, { name: string | null; until: number }>;
  /** Session → the operator's unsent text, mode and attachments. */
  drafts: Record<string, ComposerDraft>;
  setTyping: (sessionId: string, name: string | null) => void;
  clearTyping: (sessionId: string) => void;
  /** Keeps (or, when empty, forgets) the draft of a dialog. */
  setDraft: (sessionId: string, draft: ComposerDraft) => void;
  /** Forgets everything of the signed-in user (drafts, typing): called when they sign out. */
  reset: () => void;
}

const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useOpenlinesStore = create<OpenlinesState>()((set, get) => ({
  typing: {},
  drafts: {},
  setDraft: (sessionId, draft) =>
    set((state) => {
      const empty = !draft.text.trim() && draft.attachments.length === 0 && !draft.note;
      if (empty) {
        if (!(sessionId in state.drafts)) return state;
        const { [sessionId]: _, ...rest } = state.drafts;
        return { drafts: rest };
      }
      return { drafts: { ...state.drafts, [sessionId]: draft } };
    }),
  reset: () => set({ typing: {}, drafts: {} }),
  setTyping: (sessionId, name) => {
    clearTimeout(typingTimers.get(sessionId));
    typingTimers.set(
      sessionId,
      setTimeout(() => {
        typingTimers.delete(sessionId);
        get().clearTyping(sessionId);
      }, TYPING_MS),
    );
    set((state) => ({ typing: { ...state.typing, [sessionId]: { name, until: Date.now() + TYPING_MS } } }));
  },
  clearTyping: (sessionId) =>
    set((state) => {
      if (!(sessionId in state.typing)) return state;
      const { [sessionId]: _, ...rest } = state.typing;
      return { typing: rest };
    }),
}));

/** Coalesces bursts of invalidations (many dialogs changing at once). */
function debounced(action: () => void, ms: number): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(action, ms);
  };
}

/**
 * Keeps the open lines caches live (module `setup`): dialog lists and counters, the open dialog's messages, delivery
 * and read state, the client's typing, settings changes.
 */
export function setupOpenlinesRealtime(queryClient: QueryClient, realtime: RealtimeManager): void {
  // Nothing of a user (unsent drafts above all) may outlive their session in this tab.
  const userOf = (state: ReturnType<typeof getAuthState>) =>
    state.status === 'authenticated' ? (state.user?.id ?? null) : null;
  let signedIn = userOf(getAuthState());
  useAuthStore.subscribe((state) => {
    const user = userOf(state);
    if (user === signedIn) return;
    const previous = signedIn;
    signedIn = user;
    if (previous !== null) useOpenlinesStore.getState().reset();
  });

  const refreshLists = debounced(() => {
    void queryClient.invalidateQueries({ queryKey: openlinesKeys.sessions() });
    void queryClient.invalidateQueries({ queryKey: openlinesKeys.counters() });
  }, 250);

  realtime.on<SessionChangedPayload>(OpenlinesEvents.sessionChanged, (payload) => {
    refreshLists();
    void queryClient.invalidateQueries({ queryKey: openlinesKeys.session(payload.sessionId) });
  });

  realtime.on<{ sessionId: string }>(OpenlinesEvents.sessionRemoved, (payload) => {
    refreshLists();
    queryClient.removeQueries({ queryKey: openlinesKeys.messages(payload.sessionId) });
  });

  const onMessage = (message: Message) => {
    upsertMessage(queryClient, message);
    if (message.author === 'customer') useOpenlinesStore.getState().clearTyping(message.sessionId);
  };
  realtime.on<Message>(OpenlinesEvents.messageCreated, onMessage);
  realtime.on<Message>(OpenlinesEvents.messageUpdated, onMessage);

  realtime.on<SessionReadPayload>(OpenlinesEvents.sessionRead, (payload) => {
    if (payload.userId === null) {
      applyCustomerAcks(
        queryClient,
        payload.sessionId,
        payload.customerDeliveredSeq,
        payload.customerReadSeq,
      );
      queryClient.setQueryData<SessionDetails>(openlinesKeys.session(payload.sessionId), (session) =>
        session
          ? {
              ...session,
              customerDeliveredSeq: payload.customerDeliveredSeq,
              customerReadSeq: payload.customerReadSeq,
            }
          : session,
      );
    } else {
      refreshLists();
    }
  });

  realtime.on<CustomerTypingPayload>(OpenlinesEvents.typing, (payload) =>
    useOpenlinesStore.getState().setTyping(payload.sessionId, payload.name),
  );

  realtime.on(OpenlinesEvents.configChanged, () => {
    void queryClient.invalidateQueries({ queryKey: openlinesKeys.me() });
    void queryClient.invalidateQueries({ queryKey: openlinesKeys.lines() });
    void queryClient.invalidateQueries({ queryKey: openlinesKeys.counters() });
  });

  realtime.on(
    REALTIME_RECONNECTED,
    () => void queryClient.invalidateQueries({ queryKey: openlinesKeys.all }),
  );
}
